// ScopeEmissionDoughnutChart.jsx
import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Doughnut } from "react-chartjs-2";
import "chart.js/auto";
const PALETTE = [
  "#a18cd1",
  "#ff9a9e",
  "#8fd3f4",
  "#ffc3a0",
  "#b0eacd",
  "#f7a8b8",
  "#d4aef2",
  "#facd60",
  "#8ecae6",
  "#56cfe1",
  "#72efdd",
  "#caffbf",
  // …add as many as you like
];

const ScopeEmissionDoughnutChart = forwardRef(
  ({ scopeData, showTable = true, tablePosition = "below" }, ref) => {
    const [chartData, setChartData] = useState({
      labels: [],
      datasets: [
        {
          data: [],
          backgroundColor: ["#a18cd1", "#ff9a9e", "#8fd3f4"],
          borderWidth: 3,
          borderRadius: 8,
          cutout: "85%",
          borderColor: "#e4e8f6",
          hoverBackgroundColor: ["#a18cd1", "#ff9a9e", "#8fd3f4"],
          hoverBorderWidth: 3,
          hoverBorderColor: "#e4e8f6",
          hoverOffset: 6,
          spacing: 3,
        },
      ],
    });

    const options = {
      plugins: {
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.label || "";
              if (context.parsed) {
                const formattedValue = context.parsed.toFixed(2);
                label += `: ${formattedValue} KgCO₂`;
              }
              return label;
            },
          },
          backgroundColor: "rgba(0, 0, 0, 0.7)",
          titleColor: "#ffffff",
          bodyColor: "#ffffff",
          borderColor: "rgba(75, 192, 192, 1)",
          borderWidth: 1,
        },
        legend: {
          display: true,
          position: "bottom",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            padding: 12,
            color: "#36454F",
            font: { size: 12 },
            generateLabels: (chart) => {
              const datasets = chart.data.datasets;
              return chart.data.labels.map((label, i) => {
                const value = datasets[0].data[i];
                return {
                  text: `${label}: ${parseFloat(value).toFixed(2)} KgCO₂e`,
                  fillStyle: datasets[0].backgroundColor[i],
                  index: i,
                };
              });
            },
          },
          align: "start",
        },
      },
    };

    // 1) Ref so parent can call toBase64Image()
    const chartRef = useRef(null);

    // 2) Rebuild chartData whenever scopeData changes
    useEffect(() => {
      if (!scopeData) return;

      const labels = Object.keys(scopeData);
      const values = Object.values(scopeData).map((v) => parseFloat(v) || 0);

      const colours = labels.map((_, i) => PALETTE[i % PALETTE.length]);

      setChartData({
        labels,
        datasets: [
          {
            ...chartData.datasets[0],
            data: values,
            backgroundColor: colours,
            hoverBackgroundColor: colours,
          },
        ],
      });
    }, [scopeData]);

    // 3) Expose .toBase64Image() on the outer ref
    useImperativeHandle(ref, () => ({
      toBase64Image: () => {
        if (chartRef.current) {
          return chartRef.current.toBase64Image();
        }
        return null;
      },
    }));

    // Layout logic
    const isTableRight = tablePosition === "right";

    if (isTableRight) {
      // Table to the right, outside the chart container
      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            height: "450px",
          }}
        >
          <div className="chart-container" style={{ flex: 1 }}>
            <div className="chart-header">
              <h3>Scope-wise Carbon Emissions</h3>
            </div>
            <div className="chart-data" style={{ height: "380px" }}>
              <Doughnut ref={chartRef} data={chartData} options={options} />
            </div>
          </div>
          {showTable && (
            <div
              style={{
                flex: 1,
                marginLeft: "30px",
                display: "flex",
                alignItems: "center",
              }}
            >
              <div style={{ width: "100%" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "12px",
                  }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: "60%" }}>Scope</th>
                      <th style={{ width: "40%" }}>Emission (KgCO₂e)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scopeData ? (
                      Object.entries(scopeData).map(([scope, value]) => (
                        <tr key={scope}>
                          <td>{scope}</td>
                          <td>{parseFloat(value).toFixed(2)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="2">No data available</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      );
    }
    // Default: table below chart, inside chart-container
    return (
      <div className="chart-container">
        <div className="chart-header">
          <h3>Scope-wise Carbon Emissions</h3>
        </div>
        <div className="chart-data">
          <Doughnut ref={chartRef} data={chartData} options={options} />
        </div>
        {showTable && (
          <div style={{ width: "100%", marginTop: "20px" }}>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: "12px",
              }}
            >
              <thead>
                <tr>
                  <th style={{ width: "60%" }}>Scope</th>
                  <th style={{ width: "40%" }}>Emission (KgCO₂e)</th>
                </tr>
              </thead>
              <tbody>
                {scopeData ? (
                  Object.entries(scopeData).map(([scope, value]) => (
                    <tr key={scope}>
                      <td>{scope}</td>
                      <td>{parseFloat(value).toFixed(2)}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="2">No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }
);

export default ScopeEmissionDoughnutChart;
